import { SelectChangeEvent } from '@mui/material'
import { useState, useEffect, useMemo } from 'react'

import {
  TFilterOption,
  TProcedureFiltersModalLogicResult,
} from './ProcedureFilters.types'
import { Surgery } from 'common/types'
import { TProcedureFilters } from 'lib/hooks/useFilters'

// Import hospital name utilities from ProcedureListTable logic
const HOSPITAL_NAMES = [
  'General',
  'Community',
  'Childrens',
  'UCLA',
  'USC',
] as const

/**
 * Generates a deterministic hospital name based on surgery ID
 * This ensures the same surgery always gets the same hospital name
 * @param surgeryId - The unique identifier for the surgery
 * @returns A consistent hospital name for the given surgery
 */
const getHospitalNameForSurgery = (surgeryId: string): string => {
  // Create a simple hash from the surgery ID
  let hash = 0
  for (let i = 0; i < surgeryId.length; i++) {
    const char = surgeryId.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get a consistent index
  const index = Math.abs(hash) % HOSPITAL_NAMES.length
  return HOSPITAL_NAMES[index]
}

export const useProcedureFiltersModalLogic = (
  open: boolean,
  filters: TProcedureFilters,
  surgeries: Surgery[] = [],
  onApplyFilters: (filters: TProcedureFilters) => void,
  onClearFilters: () => void,
  onClose: () => void
): TProcedureFiltersModalLogicResult => {
  const [localFilters, setLocalFilters] = useState<TProcedureFilters>(filters)

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters, open])

  const locationOptions: TFilterOption[] = useMemo(() => {
    const locationSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.visit?.location?.department) {
        locationSet.add(surgery.visit.location.department)
      }
    })

    localFilters.locations.forEach((location) => {
      locationSet.add(location)
    })

    return Array.from(locationSet)
      .map((location) => ({ value: location, label: location }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.locations])

  const hospitalOptions: TFilterOption[] = useMemo(() => {
    const hospitalSet = new Set<string>()

    // Generate hospital names for all surgeries
    surgeries.forEach((surgery) => {
      const hospitalName = getHospitalNameForSurgery(surgery._id)
      hospitalSet.add(hospitalName)
    })

    // Include currently selected hospitals from filters
    localFilters.rooms.forEach((hospital) => {
      hospitalSet.add(hospital)
    })

    return Array.from(hospitalSet)
      .map((hospital) => ({ value: hospital, label: hospital }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.rooms])

  const roomOptions: TFilterOption[] = useMemo(() => {
    const roomSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.visit?.location?.room) {
        roomSet.add(surgery.visit.location.room)
      }
    })

    localFilters.rooms.forEach((room) => {
      roomSet.add(room)
    })

    return Array.from(roomSet)
      .map((room) => ({ value: room, label: `Room ${room}` }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.rooms])

  const surgeonOptions: TFilterOption[] = useMemo(() => {
    const surgeonSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.visit?.attendingProvider) {
        const provider = surgery.visit.attendingProvider
        const fullName = `${provider.firstName || ''} ${
          provider.lastName || ''
        }`.trim()
        if (fullName) surgeonSet.add(fullName)
      }
    })

    if (localFilters.surgeonName) {
      surgeonSet.add(localFilters.surgeonName)
    }

    return Array.from(surgeonSet)
      .map((surgeon) => ({ value: surgeon, label: surgeon }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.surgeonName])

  const surgeryNameOptions: TFilterOption[] = useMemo(() => {
    const surgeryNameSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.procedures && surgery.procedures.length > 0) {
        surgery.procedures.forEach((procedure) => {
          if (procedure.description) {
            surgeryNameSet.add(procedure.description)
          }
        })
      }
    })

    if (localFilters.surgeryName) {
      surgeryNameSet.add(localFilters.surgeryName)
    }

    return Array.from(surgeryNameSet)
      .map((name) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.surgeryName])

  const handleArrayFilterChange = (
    event: SelectChangeEvent<string[]>,
    filterKey: 'locations' | 'rooms'
  ) => {
    const {
      target: { value },
    } = event

    const selectedValues = typeof value === 'string' ? value.split(',') : value

    setLocalFilters((prev) => ({
      ...prev,
      [filterKey]: selectedValues,
    }))
  }

  const handleApply = () => {
    onApplyFilters(localFilters)
    onClose()
  }

  const handleClear = () => {
    onClearFilters()
    onClose()
  }

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 225,
        width: 250,
      },
    },
  }

  return {
    MenuProps,
    roomOptions,
    hospitalOptions,
    localFilters,
    surgeonOptions,
    locationOptions,
    surgeryNameOptions,
    handleApply,
    handleClear,
    setLocalFilters,
    handleArrayFilterChange,
  }
}
